import type { Block } from '@ttab/elephant-api/newsdoc'
import type { TBElement, TBText } from '@ttab/textbit'
import { transformFactbox } from '@/shared/transformations/newsdoc/core/factbox'

export type SyncState = 'unknown' | 'inSync' | 'outOfSync'

type SlateNode = TBElement | TBText

const isText = (node: SlateNode): node is TBText => {
  return !('children' in node)
}

/** Marks are the keys a text node carries besides its `text`, e.g. `core/bold`. */
const marksOf = (node: TBText): string => {
  return Object.keys(node).filter((key) => key !== 'text').sort().join('|')
}

/**
 * Joins neighbouring text nodes that carry the same marks.
 *
 * Deserializing splits text at every element boundary, so markup the editor does
 * not support - `<b>` say, which `deserializeNode` drops - leaves the original as
 * two nodes where its copy, re-serialized without the tag, holds one. Same text,
 * same marks, so it must not read as an edit.
 */
const mergeAdjacentText = (nodes: SlateNode[]): SlateNode[] => {
  return nodes.reduce<SlateNode[]>((merged, node) => {
    const previous = merged[merged.length - 1]

    if (previous && isText(node) && isText(previous) && marksOf(node) === marksOf(previous)) {
      merged[merged.length - 1] = { ...previous, text: previous.text + node.text }
      return merged
    }

    merged.push(node)
    return merged
  }, [])
}

/**
 * Drops `id` from a slate tree. Node ids are per-copy uuids: an embed and the
 * factbox it was copied from describe the same contents with different ids.
 */
const stripIds = (nodes: SlateNode[]): unknown[] => {
  return mergeAdjacentText(nodes).map((node) => {
    if (isText(node)) {
      return node
    }

    const { id, children, ...rest } = node

    return {
      ...rest,
      children: stripIds(children ?? [])
    }
  })
}

/**
 * Comparable form of a factbox's title and body.
 *
 * Both sides go through `transformFactbox`, so the comparison is symmetrical and
 * survives differences that aren't a real edit: node ids, entity escaping and url
 * encoding in `data.text`, and markup the editor drops on the way in. Real edits -
 * text, formatting, link urls, added or removed blocks, text roles - still change
 * the fingerprint.
 *
 * Only `children` is used. `properties` is derived from the block's `rel: source`
 * link, which is expected to differ between an embed and its original.
 *
 * Returns `undefined` when the contents can't be transformed, e.g. an unknown
 * block type, so one odd embed can't take down the whole list.
 */
export const factboxContentFingerprint = (block: Block): string | undefined => {
  try {
    return JSON.stringify(stripIds(transformFactbox(block).children))
  } catch {
    return undefined
  }
}

/**
 * Sync state per article for one factbox document.
 *
 * Compares the contents of each article's embedded copy against the factbox's own
 * contents, rather than the `data.original_version` stamp: only the drag-drop path
 * ever writes that stamp, so embeds predating it - and those created from the
 * content menu - could never be told apart from stale ones.
 *
 * Works off the articles' actual `core/factbox` blocks because the index flattens
 * repeated block subfields across every factbox in an article, which makes it
 * impossible to tell which value belongs to `sourceId`.
 */
export function getSyncStateByArticle(
  blocksByArticle: Map<string, Block[]> | undefined,
  sourceId: string,
  originalFingerprint: string | undefined
): Map<string, SyncState> {
  const result = new Map<string, SyncState>()

  if (!blocksByArticle || !originalFingerprint) {
    return result
  }

  for (const [articleId, blocks] of blocksByArticle) {
    if (!articleId) {
      continue
    }

    // `rel: 'source'` is what revertFactbox writes for the originating document.
    const embeds = blocks.filter((block) => {
      return block.links.find((link) => link.rel === 'source')?.uuid === sourceId
    })

    // No embed of this factbox left in the article - nothing to compare.
    if (!embeds.length) {
      result.set(articleId, 'unknown')
      continue
    }

    const fingerprints = embeds.map((block) => factboxContentFingerprint(block))

    // Any diverging copy makes the article stale, even if another copy matches.
    const isOutOfSync = fingerprints.some((fingerprint) => {
      return fingerprint !== undefined && fingerprint !== originalFingerprint
    })

    if (isOutOfSync) {
      result.set(articleId, 'outOfSync')
      continue
    }

    // A copy that couldn't be read can't be vouched for.
    const hasUnreadable = fingerprints.some((fingerprint) => fingerprint === undefined)

    result.set(articleId, hasUnreadable ? 'unknown' : 'inSync')
  }

  return result
}
